/**
 * Google Analytics 4 Event Tracking Utility
 * Audio & Co® - Conversion Tracking
 */

// Check if gtag is available
const isGtagAvailable = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * Track a custom event in GA4
 */
export const trackEvent = (eventName, params = {}) => {
  if (isGtagAvailable()) {
    window.gtag('event', eventName, params);
    console.log(`[GA4] Event tracked: ${eventName}`, params);
  }
};

/**
 * Track form submissions (CONVERSION)
 */
export const trackFormSubmit = (formName, formData = {}) => {
  trackEvent('form_submit', {
    event_category: 'conversion',
    event_label: formName,
    form_name: formName,
    ...formData
  });
  
  // Also track as a conversion event
  trackEvent('generate_lead', {
    event_category: 'conversion',
    event_label: formName,
    currency: 'GBP',
    value: 50 // Estimated lead value
  });
};

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (ctaName, ctaLocation) => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    event_label: ctaName,
    cta_name: ctaName,
    cta_location: ctaLocation
  });
};

/**
 * Track booking/scheduling link clicks (CONVERSION)
 */
export const trackBookingClick = (bookingType = 'discovery_call') => {
  trackEvent('booking_click', {
    event_category: 'conversion',
    event_label: bookingType,
    booking_type: bookingType
  });
  
  // Track as a conversion
  trackEvent('schedule_appointment', {
    event_category: 'conversion',
    event_label: bookingType
  });
};

/**
 * Track WhatsApp button clicks (CONVERSION)
 */
export const trackWhatsAppClick = () => {
  trackEvent('whatsapp_click', {
    event_category: 'conversion',
    event_label: 'whatsapp_contact',
    contact_method: 'whatsapp'
  });
};

/**
 * Track phone number clicks (CONVERSION)
 */
export const trackPhoneClick = (phoneNumber) => {
  trackEvent('phone_click', {
    event_category: 'conversion',
    event_label: phoneNumber,
    contact_method: 'phone'
  });
};

/**
 * Track email link clicks
 */
export const trackEmailClick = (email) => {
  trackEvent('email_click', {
    event_category: 'engagement',
    event_label: email,
    contact_method: 'email'
  });
};

/**
 * Track service/package views
 */
export const trackServiceView = (serviceName, serviceCategory, price) => {
  trackEvent('view_item', {
    event_category: 'ecommerce',
    event_label: serviceName,
    items: [{
      item_name: serviceName,
      item_category: serviceCategory,
      price: price,
      currency: 'GBP'
    }]
  });
};

/**
 * Track blog post reads
 */
export const trackBlogRead = (postTitle, postCategory, author) => {
  trackEvent('blog_read', {
    event_category: 'content',
    event_label: postTitle,
    post_title: postTitle,
    post_category: postCategory,
    author: author
  });
};

/**
 * Track PDF/resource downloads
 */
export const trackDownload = (fileName, fileType) => {
  trackEvent('file_download', {
    event_category: 'engagement',
    event_label: fileName,
    file_name: fileName,
    file_type: fileType
  });
};

/**
 * Track video plays
 */
export const trackVideoPlay = (videoName, videoSource) => {
  trackEvent('video_play', {
    event_category: 'engagement',
    event_label: videoName,
    video_name: videoName,
    video_source: videoSource
  });
};

/**
 * Track social share clicks
 */
export const trackSocialShare = (platform, contentType, contentTitle) => {
  trackEvent('share', {
    event_category: 'social',
    event_label: platform,
    method: platform,
    content_type: contentType,
    item_id: contentTitle
  });
};

/**
 * Track page timing (for performance monitoring)
 */
export const trackPageTiming = () => {
  if (typeof window !== 'undefined' && window.performance) {
    const timing = window.performance.timing;
    const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
    
    if (pageLoadTime > 0) {
      trackEvent('page_timing', {
        event_category: 'performance',
        event_label: window.location.pathname,
        value: pageLoadTime,
        page_load_time: pageLoadTime
      });
    }
  }
};

/**
 * Track exit intent popup interactions
 */
export const trackExitIntent = (action) => {
  trackEvent('exit_intent', {
    event_category: 'engagement',
    event_label: action, // 'shown', 'closed', 'converted'
    action: action
  });
};

/**
 * Track pricing page interactions
 */
export const trackPricingView = (packageName, price) => {
  trackEvent('view_pricing', {
    event_category: 'ecommerce',
    event_label: packageName,
    package_name: packageName,
    price: price,
    currency: 'GBP'
  });
};

/**
 * Track search queries (if site has search)
 */
export const trackSearch = (searchTerm, resultsCount) => {
  trackEvent('search', {
    event_category: 'engagement',
    event_label: searchTerm,
    search_term: searchTerm,
    results_count: resultsCount
  });
};

/**
 * Set user properties (for audience building)
 */
export const setUserProperties = (properties) => {
  if (isGtagAvailable()) {
    window.gtag('set', 'user_properties', properties);
  }
};

export default {
  trackEvent,
  trackFormSubmit,
  trackCTAClick,
  trackBookingClick,
  trackWhatsAppClick,
  trackPhoneClick,
  trackEmailClick,
  trackServiceView,
  trackBlogRead,
  trackDownload,
  trackVideoPlay,
  trackSocialShare,
  trackPageTiming,
  trackExitIntent,
  trackPricingView,
  trackSearch,
  setUserProperties
};
